import { useCallback } from "react";
import { appById, type DesktopAppId } from "./apps";
import { windowId, type DesktopWindowState } from "./layout";
import { clampWindowBounds, type WorkArea } from "./windowGeometry";

export const workspaceLayoutKey = (userId: string) => `base-sul-os:workspace-layout:v1:${userId}`;

export function saveWorkspaceLayout(userId: string, windows: DesktopWindowState[], storage?: Pick<Storage, "setItem">): boolean {
  if (!userId) return false;
  try {
    const layout = windows.map(({ appId, instanceId, minimized, maximized, z, bounds, snap }) => ({ appId, instanceId, minimized, maximized, z, bounds, snap }));
    (storage ?? window.localStorage).setItem(workspaceLayoutKey(userId), JSON.stringify({ version: 1, windows: layout }));
    return true;
  } catch { return false; }
}

/** Explicit restore updates existing authorized windows only; it never launches an app. */
export function restoreWorkspaceLayout(userId: string, current: DesktopWindowState[], allowed: Set<DesktopAppId>, area: WorkArea, storage?: Pick<Storage, "getItem">): DesktopWindowState[] {
  let parsed: { version?: unknown; windows?: unknown } | null;
  try { parsed = JSON.parse((storage ?? window.localStorage).getItem(workspaceLayoutKey(userId)) ?? "null"); }
  catch { throw new Error("Não foi possível ler a disposição salva neste navegador."); }
  if (!userId || parsed?.version !== 1 || !Array.isArray(parsed.windows)) throw new Error("Nenhuma disposição foi salva neste navegador para esta conta.");
  const saved = new Map<string, DesktopWindowState>();
  for (const candidate of parsed.windows.slice(0, 100)) {
    if (!candidate || typeof candidate !== "object" || !appById(candidate.appId) || !allowed.has(candidate.appId)) continue;
    const bounds = candidate.bounds;
    saved.set(windowId(candidate), {
      appId: candidate.appId,
      minimized: candidate.minimized === true,
      maximized: candidate.maximized === true,
      z: Number.isSafeInteger(candidate.z) ? Math.max(0, Math.min(candidate.z, 10_000)) : 0,
      snap: candidate.snap === "left" || candidate.snap === "right" ? candidate.snap : null,
      bounds: bounds && [bounds.x, bounds.y, bounds.width, bounds.height].every((value) => typeof value === "number" && Number.isFinite(value))
        ? clampWindowBounds(bounds, area) : undefined,
    });
  }
  return current.filter((item) => allowed.has(item.appId)).map((item) => {
    const restore = saved.get(windowId(item));
    return restore ? { ...item, ...restore, bounds: restore.bounds ?? item.bounds } : item;
  });
}

export function useWorkspaceLayout(userId: string) {
  const save = useCallback((windows: DesktopWindowState[]) => saveWorkspaceLayout(userId, windows), [userId]);
  const restore = useCallback((windows: DesktopWindowState[], allowed: Set<DesktopAppId>, area: WorkArea) => restoreWorkspaceLayout(userId, windows, allowed, area), [userId]);
  return { save, restore };
}
